import { asMarionetteView } from "lib/common/hoc/BackboneReact/asMarionetteView";
import { default as baseServices } from "lib/BaseServices";
import { default as services } from "lib/Services";
import { DataQualityFormContainer as Form } from "../views/DataQualityForm";
import { BackboneDataQualityResultsLayout as Results } from "../views/results/DataQualityResultsLayout";
import promisify from "lib/promisify";
import withOver, { appAlert } from "lib/withOver";
import SearchResults from "models/SearchResults";
import DataQualityResultsCollection from "models/DataQualityResultsCollection";
import sortsToOrder from "lib/sortsToOrder";
import query_string from "query_string";
import userStore from "data/userStore";
import organizationsStore from "data/organizationsStore";

const MarionetteResults = asMarionetteView(Results);

class DqrController extends Backbone.Controller {
    constructor(...args) {
        super(...args);
        this.initialize = this.initialize.bind(this);
        this.onLoadHome = this.onLoadHome.bind(this);
        this.onLoadDetails = this.onLoadDetails.bind(this);
        this.onSearch = this.onSearch.bind(this);
        this.getSearchResults = this.getSearchResults.bind(this);
    }

    initialize(application) {
        this.app = application;
    }

    onLoadHome() {
        const model = this.app.model.get("dataQualityReport");
        const sb = new Form({model});
        this.app.layout.drawer.show(sb);

        const fields = model.get("stereotype").get("fields");
        const rv = new MarionetteResults({
            model,
            fields,
            className: "empty-dqr",
        });
        this.app.layout.content.show(rv);
    }

    onLoadDetails(model) {
        const data = model.get("data");
        if (data) {
            const mrc = model.get("magicRootCoordinates");
            const ekey = model.get("key");
            const eid = model.get("id");
            const org = data._owner;
            const {
                key,
            } = mrc;
            const {
                id,
            } = mrc;
            const shortname = data._shortname;
            this.dispatch(
                "navigate",
                org,
                key,
                id,
                ekey,
                eid,
                shortname,
                true,
            );
        }
    }

    onSearch(model, collection) {
        this.dispatch("user:activity");

        const page = model.get("page");
        const pageSize = model.get("pageSize");

        // Calculate the page we need on the server results.
        const serverSearchResults = model.get("serverSearchResults");

        const serverPage = serverSearchResults.get("page");
        const serverPageSize = serverSearchResults.get("pageSize");

        // We take the ceiling because the server side pagination is 1 indexed.
        const requestedServerPage = Math.ceil((page * pageSize) / (serverPageSize));

        // Check to see if page requested is in memory.
        if (serverSearchResults.get("results")
            .isEmpty() || (requestedServerPage !== serverPage)) {
            // We need to load data from server
            this.getSearchResults(
                model,
                requestedServerPage,
                serverPageSize,
            ).then(() => this.loadInMemoryResults(model, page, pageSize));
        } else {
            // We have the results in memory.
            this.loadInMemoryResults(model, page, pageSize);
        }
    }

    loadInMemoryResults(model, page, pageSize) {
        const serverSearchResults = model.get("serverSearchResults");
        const totalResultCount = serverSearchResults.get("totalResultCount");
        const serverPageSize = serverSearchResults.get("pageSize");

        const results = serverSearchResults.get("results");
        const begin = (((page - 1) * pageSize) % serverPageSize);
        const end = begin + pageSize;

        const clientSearchResults = results.slice(begin, end);
        const clientSearchResultsCollection = new DataQualityResultsCollection(
            clientSearchResults);

        // Estimate the total number of client side pages
        const pageCount = Math.ceil(totalResultCount / pageSize);

        const resultsModel = new Backbone.Model({
            results: clientSearchResultsCollection,
            page,
            pageSize,
            pageCount,
        });

        model.set("searchResults", resultsModel);
    }

    searchAll(stereotype, q, code, page, pageSize, sorts, owners) {
        // this is just a wrapper to line up searchEntity parameters with
        // loadDQRDetail and loadDQRSummary
        // 'code' is ignored and stereotype key is extracted.
        // everything else should be the same.
        return services.searchEntity(
            q,
            sorts,
            stereotype.get("key"),
            pageSize,
            page,
            false,
            owners,
        );
    }

    _getOwners(model) {
        const owners = model.get("owners");
        if (owners.some(it => it === "")) {
            return "";
        }
        return [...new Set(owners)].sort().join(",");
    }

    getSearchResults(model, page, pageSize) {
        const q = model.get("searchQuery").slice(0);
        const owners = this._getOwners(model);
        const stereotype = model.get("stereotype");

        const s = model.get("sorts");
        const sorts = sortsToOrder(s.attributes);
        const rt = model.getReportTypeKey();
        const service = (rt === "d") ? services.loadDQRDetail : (rt === "a") ? this.searchAll : services.loadDQRSummary;

        const code = model.getValidationTypeCode();

        return withOver("Searching...", () =>
            promisify(
                service(
                    stereotype,
                    q,
                    code,
                    page,
                    pageSize,
                    sorts,
                    owners,
                ),
            ).then(function (data) {
                    if (userStore.isAggregate()) {
                        const byKey = organizationsStore.getKeyLookup();
                        data = {
                            ...data,
                            results: data.results.map(r => ({
                                ...r,
                                data: {
                                    ...r.data,
                                    ao_owner_name: byKey.hasOwnProperty(r.owner)
                                        ? byKey[r.owner].name
                                        : r.owner,
                                },
                            })),
                        };
                    }
                    data.results = new DataQualityResultsCollection(data.results);
                    const searchResults = new SearchResults(data);
                    return model.set("serverSearchResults", searchResults);
                }
                , function (jqXHR) {
                    if (jqXHR.status < 500) return;
                    console.error(jqXHR);
                    return appAlert("An error has occurred.");
                }));
    }

    onExport(model) {
        this.dispatch("user:activity");
        const stereotype = model.get("stereotype");
        const q = model.get("searchQuery");
        const owners = this._getOwners(model);
        const code = model.getValidationTypeCode();

        const s = model.get("sorts");
        const sorts = sortsToOrder(s.attributes);

        const rt = model.getReportTypeKey();

        const data = query_string.serialize({
            q,
            code,
            o: sorts,
            p: 1,
            ps: 1000000000,
            owners,
        });

        let path;
        if (rt === "d") {
            path = ["dqr", stereotype.get("key"), "fields"];
        } else if (rt === "a") {
            path = ["search", stereotype.get("key")];
        } else {
            path = ["dqr", stereotype.get("key")];
        }

        window.open(baseServices.url(path) + ".xls?" + data, "_blank");
    }

    setModelAttribute(model, attr, val) {
        model.set(attr, val);
    }
}

DqrController.prototype.events = {
    "search:dataQualityReport": "onSearch",
    "load:dqr_home": "onLoadHome",
    "load:dataQualityDetails": "onLoadDetails",
    "export:dataQualityReport": "onExport",
    "set:dqr:model:attribute": "setModelAttribute",
};

export default DqrController;



// WEBPACK FOOTER //
// ./app/lib/dqr/controllers/DqrController.js